import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./PublicLeaderboard.css";
import Layout from "../components/Layout/Layout";

const money = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "0";
  return value.toLocaleString("en-IN");
};

const rupee = (n) => `\u20B9${money(n)}`;

const getInitial = (name) => {
  const trimmed = String(name || "User").trim();
  return (trimmed[0] || "U").toUpperCase();
};

const normalizeUser = (user, index) => {
  const spend =
    user?.spend ??
    user?.totalSpend ??
    user?.totalSpent ??
    user?.amount ??
    user?.total_purchase ??
    0;

  return {
    rank: Number(user?.rank) || index + 1,
    name: user?.name || user?.fname || user?.username || "User",
    spend: Number(spend) || 0,
    picture: user?.picture || user?.avatar || user?.image || "",
  };
};

const getCountdownTarget = (month) => {
  const [year, monthIndex] = String(month || "").split("-").map(Number);

  if (year && monthIndex >= 1 && monthIndex <= 12) {
    return new Date(year, monthIndex, 1, 0, 0, 0, 0).getTime();
  }

  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0).getTime();
};

const getCountdown = (month, now) => {
  const diff = Math.max(0, getCountdownTarget(month) - now);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
};

const PodiumAvatar = ({ user, rank }) => (
  <div className={`plAvatar plAvatar--${rank}`}>
    {user.picture ? <img src={user.picture} alt={user.name} /> : <span>{getInitial(user.name)}</span>}
  </div>
);

const PodiumCard = ({ user, rank, label }) => {
  if (!user) return null;

  return (
    <div className={`plPodiumCard plPodiumCard--${rank}`}>
      <div className={`plPlaceBadge plPlaceBadge--${rank}`}>{label}</div>
      <PodiumAvatar user={user} rank={rank} />
      <div className="plPodiumName">{user.name}</div>
      <div className="plPodiumSpend">{rupee(user.spend)}</div>
    </div>
  );
};

const RankAvatar = ({ user }) => (
  <div className="plListAvatar">
    {user.picture ? <img src={user.picture} alt={user.name} /> : <span>{getInitial(user.name)}</span>}
  </div>
);

export default function PublicLeaderboard() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("top");
  const [now, setNow] = useState(Date.now());

  const fetchLB = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await axios.get("/api/leaderboard/monthly-spend-public");
      if (!res.data?.success) {
        setErr(res.data?.message || "Failed to load leaderboard");
        return;
      }
      setData(res.data?.data || null);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || e?.message || "Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLB();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const month = data?.month || "";
  const topUsers = useMemo(
    () => (Array.isArray(data?.topUsers) ? data.topUsers.map(normalizeUser) : []),
    [data]
  );

  const countdown = useMemo(() => getCountdown(month, now), [month, now]);
  const first = topUsers[0];
  const second = topUsers[1];
  const third = topUsers[2];
  const listUsers = topUsers.slice(3);

  return (
    <Layout>
      <main className="plWrap">
        <section className="plStage">
          <div className="plTabs" role="tablist" aria-label="Leaderboard sections">
            <button
              type="button"
              className={`plTab ${activeTab === "top" ? "is-active" : ""}`}
              onClick={() => setActiveTab("top")}
            >
              Top Spenders
            </button>
            <button
              type="button"
              className={`plTab ${activeTab === "past" ? "is-active" : ""}`}
              onClick={() => setActiveTab("past")}
            >
              Past Rewards
            </button>
          </div>

          {err ? <div className="plError">{err}</div> : null}

          {activeTab === "top" ? (
            <>
              <div className="plCountdown" aria-label="Leaderboard month countdown">
                <span>ENDS IN</span>
                <b>{countdown.days}d</b>
                <i>:</i>
                <b>{countdown.hours}h</b>
                <i>:</i>
                <b>{countdown.minutes}m</b>
                <i>:</i>
                <b>{countdown.seconds}s</b>
              </div>

              {loading && !topUsers.length ? (
                <div className="plEmptyCard">Loading leaderboard...</div>
              ) : topUsers.length ? (
                <>
                  <div className="plPodium" aria-label="Top three spenders">
                    <div className="plPodiumSlot">
                      <PodiumCard user={second} rank={2} label="2nd" />
                    </div>
                    <div className="plPodiumSlot">
                      <PodiumCard user={first} rank={1} label="1st" />
                    </div>
                    <div className="plPodiumSlot">
                      <PodiumCard user={third} rank={3} label="3rd" />
                    </div>
                  </div>

                  {listUsers.length ? (
                    <div className="plRankList" aria-label="Leaderboard rankings">
                      {listUsers.map((user) => (
                        <div className="plRankRow" key={`${user.rank}-${user.name}-${user.spend}`}>
                          <div className="plRankNumber">#{user.rank}</div>
                          <RankAvatar user={user} />
                          <div className="plRankInfo">
                            <div className="plRankName">{user.name}</div>
                            <div className="plRankLabel">Total Spent</div>
                          </div>
                          <div className="plRankAmount">{rupee(user.spend)}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="plEmptyCard">No leaderboard data yet.</div>
              )}
            </>
          ) : (
            <div className="plPastPanel">
              <div className="plPastCard">
                <span>Past Rewards</span>
                <strong>Coming Soon</strong>
                <p>Closed month winners and reward history will show here once the monthly contest is completed.</p>
              </div>
            </div>
          )}
        </section>
      </main>
    </Layout>
  );
}
